import React, { useEffect, useRef, useState } from "react";
import {
  Calendar,
  Repeat,
  MapPin,
  ExternalLink,
  Loader2,
  CalendarDays,
  CalendarPlus,
  Download,
} from "lucide-react";
import {
  fetchEvents,
  formatEventDate,
  pickField,
  eventCategoryLabel,
  EVENT_CATEGORY_META,
} from "@/api/events";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";
import {
  downloadIcs,
  buildGoogleCalendarUrl,
} from "@/utils/calendar";
import { Rss, Copy, Check, Share2, Facebook } from "lucide-react";

// Simpel WhatsApp-groen icoon (lucide heeft geen officieel WhatsApp-logo).
const WhatsAppIconSmall = ({ className = "w-3.5 h-3.5" }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.12 1.6 5.92L0 24l6.4-1.68a11.83 11.83 0 0 0 5.65 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.23-6.15-3.39-8.43ZM17.61 14.3c-.3-.15-1.79-.88-2.07-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.22-.65.07-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.8-1.68-2.1-.18-.3-.02-.46.13-.61.13-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.64-.93-2.24-.25-.6-.5-.52-.68-.53l-.58-.01c-.2 0-.53.08-.8.38s-1.06 1.04-1.06 2.53c0 1.5 1.08 2.94 1.23 3.14.15.2 2.12 3.25 5.14 4.55.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.79-.73 2.04-1.44.25-.72.25-1.32.18-1.45-.08-.13-.28-.2-.58-.35Z" />
  </svg>
);

const SHARE_LABELS = { nl: "Deel dit event", de: "Event teilen", en: "Share this event" };
const SHARE_WA = { nl: "Deel via WhatsApp", de: "Über WhatsApp teilen", en: "Share via WhatsApp" };
const SHARE_FB = { nl: "Deel via Facebook", de: "Über Facebook teilen", en: "Share via Facebook" };
const SHARE_OP = { nl: "op", de: "auf", en: "on" };

const EventShareButtons = ({ ev, lang, title }) => {
  const langPart = lang === "nl" ? "" : `/${lang}`;
  // Crawler-URL wijst naar het backend OG-endpoint (server-side gerenderde OG-tags).
  const crawlerUrl = BACKEND_URL
    ? `${BACKEND_URL}/api/share${langPart}/evenement/${ev.slug}`
    : `${langPart}/agenda#${ev.slug}`;
  const shareText = `${title} ${SHARE_OP[lang] || SHARE_OP.nl} Lemmer Leeft — ${crawlerUrl}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(crawlerUrl)}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const onNative = async () => {
    try {
      await navigator.share({ title, text: `${title} ${SHARE_OP[lang] || SHARE_OP.nl} Lemmer Leeft`, url: crawlerUrl });
    } catch { /* stil */ }
  };
  const iconBtn = "inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#E4EEF5] bg-white text-[#173A5E] hover:bg-[#F4F8FB] hover:border-[#6BC0E8] transition";
  return (
    <div data-testid={`event-share-${ev.slug}`} className="flex items-center gap-2">
      <span className="text-xs font-[Poppins] font-semibold uppercase tracking-[0.14em] text-[#5A7594] mr-1">
        {SHARE_LABELS[lang] || SHARE_LABELS.nl}
      </span>
      {canNativeShare && (
        <button
          type="button"
          onClick={onNative}
          data-testid={`event-share-native-${ev.slug}`}
          aria-label={SHARE_LABELS[lang] || SHARE_LABELS.nl}
          className={`${iconBtn} sm:hidden`}
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>
      )}
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`event-share-wa-${ev.slug}`}
        aria-label={SHARE_WA[lang] || SHARE_WA.nl}
        title={SHARE_WA[lang] || SHARE_WA.nl}
        className={`${iconBtn} hover:text-[#25D366]`}
      >
        <WhatsAppIconSmall />
      </a>
      <a
        href={fbHref}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`event-share-fb-${ev.slug}`}
        aria-label={SHARE_FB[lang] || SHARE_FB.nl}
        title={SHARE_FB[lang] || SHARE_FB.nl}
        className={`${iconBtn} hover:text-[#1877F2]`}
      >
        <Facebook className="w-3.5 h-3.5" />
      </a>
    </div>
  );
};

const SUBSCRIBE_LABELS = { nl: "Abonneer op de agenda", de: "Kalender abonnieren", en: "Subscribe to calendar" };
const COPY_ICS_LABELS = { nl: "Kopieer agenda-link", de: "Kalender-Link kopieren", en: "Copy calendar link" };
const COPIED_LABELS = { nl: "Gekopieerd!", de: "Kopiert!", en: "Copied!" };
const SUBSCRIBE_HINT = {
  nl: "Voeg de complete Lemmer-agenda toe aan je eigen kalender-app (Apple, Google, Outlook). Nieuwe events verschijnen dan automatisch.",
  de: "Fügen Sie den kompletten Lemmer-Kalender zu Ihrer eigenen Kalender-App hinzu (Apple, Google, Outlook). Neue Events erscheinen automatisch.",
  en: "Add the full Lemmer agenda to your own calendar app (Apple, Google, Outlook). New events appear automatically.",
};

// Backend base host (voor iCal-feed). Zonder trailing slash.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const ICS_HTTPS_URL = BACKEND_URL ? `${BACKEND_URL}/api/agenda.ics` : "/api/agenda.ics";
const ICS_WEBCAL_URL = ICS_HTTPS_URL.replace(/^https?:\/\//, "webcal://");

const SubscribeButtons = ({ lang }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(ICS_HTTPS_URL);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* stil */ }
  };
  return (
    <div
      data-testid="agenda-subscribe-block"
      className="mt-6 flex flex-wrap items-center gap-3"
    >
      <a
        href={ICS_WEBCAL_URL}
        data-testid="agenda-subscribe-btn"
        aria-label={SUBSCRIBE_LABELS[lang] || SUBSCRIBE_LABELS.nl}
        className="inline-flex items-center gap-2 bg-[#173A5E] text-white hover:bg-[#0F2A46] px-4 py-2.5 rounded-full font-[Poppins] font-semibold text-sm transition shadow-sm"
      >
        <Rss className="w-4 h-4" />
        {SUBSCRIBE_LABELS[lang] || SUBSCRIBE_LABELS.nl}
      </a>
      <button
        type="button"
        onClick={onCopy}
        data-testid="agenda-copy-ics-btn"
        aria-label={COPY_ICS_LABELS[lang] || COPY_ICS_LABELS.nl}
        className="inline-flex items-center gap-2 bg-white text-[#173A5E] border border-[#E4EEF5] hover:border-[#6BC0E8] px-4 py-2.5 rounded-full font-[Poppins] font-semibold text-sm transition"
      >
        {copied ? <Check className="w-4 h-4 text-[#2E9CD6]" /> : <Copy className="w-4 h-4" />}
        {copied ? (COPIED_LABELS[lang] || COPIED_LABELS.nl) : (COPY_ICS_LABELS[lang] || COPY_ICS_LABELS.nl)}
      </button>
    </div>
  );
};

const AGENDA_SEO = {
  nl: { title: "Agenda Lemmer — evenementen, markten en festivals", description: "Alle evenementen in Lemmer op één rij: visserijdagen, Lemsterweek, Skûtsjesilen, Beach Festival, weekmarkt en meer. Zet ze in je agenda." },
  de: { title: "Veranstaltungen Lemmer — Events, Märkte und Festivals", description: "Alle Veranstaltungen in Lemmer auf einen Blick: Fischereitage, Lemsterweek, Skûtsjesilen, Beach Festival, Wochenmarkt und mehr." },
  en: { title: "Events Lemmer — festivals, markets and more", description: "All events in Lemmer at a glance: Fishery Days, Lemsterweek, Skûtsjesilen, Beach Festival, weekly market and more." },
};

const LOAD_LABELS = { nl: "Agenda laden…", de: "Veranstaltungen laden…", en: "Loading events…" };
const ERROR_LABELS = { nl: "Kon de agenda niet laden. Probeer het zo nog eens.", de: "Veranstaltungen konnten nicht geladen werden. Bitte später erneut versuchen.", en: "Couldn't load events. Please try again." };
const EMPTY_TITLE = { nl: "Nog geen evenementen", de: "Noch keine Veranstaltungen", en: "No events yet" };
const EMPTY_BODY = { nl: "Zodra er nieuwe evenementen bekend zijn, verschijnen ze hier.", de: "Sobald neue Veranstaltungen bekannt sind, erscheinen sie hier.", en: "As soon as new events are announced, they'll appear here." };
const RECURRING_LABELS = { nl: "Terugkerend", de: "Wiederkehrend", en: "Recurring" };
const ON_REQUEST_LABELS = { nl: "Op afroep", de: "Auf Anfrage", en: "On request" };
const MORE_INFO_LABELS = { nl: "Meer info", de: "Mehr Infos", en: "More info" };
const BEKIJK_LOC_LABELS = { nl: "Bekijk locatie", de: "Ort ansehen", en: "View location" };
const ADD_CAL_LABELS = { nl: "Zet in mijn agenda", de: "Zu meinem Kalender", en: "Add to my calendar" };
const ICS_LABELS = { nl: "Apple / Outlook (.ics)", de: "Apple / Outlook (.ics)", en: "Apple / Outlook (.ics)" };
const GCAL_LABELS = { nl: "Google Agenda", de: "Google Kalender", en: "Google Calendar" };

const buildGmapsHref = (location) => {
  if (!location) return null;
  const clean = location.trim();
  // "Lemmer" toevoegen als niet al aanwezig, zodat Google Maps de juiste plaats vindt.
  const query = /lemmer/i.test(clean) ? clean : `${clean}, Lemmer`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

const ContentEyebrow = ({ children }) => (
  <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
    {children}
  </span>
);

const AccentLine = () => (
  <div className="flex items-center gap-3 mb-6" aria-hidden="true">
    <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
    <svg width="60" height="10" viewBox="0 0 60 10" className="text-[#6BC0E8]">
      <path
        d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const AddToCalendarButton = ({ ev, lang, title, description, location }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const gcalHref = buildGoogleCalendarUrl(ev, { title, description, location });
  if (!gcalHref) return null;

  const onIcs = () => {
    downloadIcs(ev, { title, description, location });
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`agenda-addcal-${ev.slug}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ADD_CAL_LABELS[lang] || ADD_CAL_LABELS.nl}
        className="inline-flex items-center gap-1.5 text-[#2E9CD6] hover:text-[#173A5E] font-[Poppins] font-semibold text-sm transition"
      >
        <CalendarPlus className="w-4 h-4" />
        {ADD_CAL_LABELS[lang] || ADD_CAL_LABELS.nl}
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`agenda-addcal-menu-${ev.slug}`}
          className="absolute z-30 mt-2 min-w-[220px] bg-white rounded-xl shadow-xl border border-[#E4EEF5] py-1.5 left-0"
        >
          <a
            href={gcalHref}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => setOpen(false)}
            data-testid={`agenda-addcal-google-${ev.slug}`}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-[Poppins] font-medium text-[#173A5E] hover:bg-[#F4F8FB] transition"
          >
            <ExternalLink className="w-4 h-4 text-[#2E9CD6]" />
            {GCAL_LABELS[lang] || GCAL_LABELS.nl}
          </a>
          <button
            type="button"
            onClick={onIcs}
            data-testid={`agenda-addcal-ics-${ev.slug}`}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-[Poppins] font-medium text-[#173A5E] hover:bg-[#F4F8FB] transition text-left"
          >
            <Download className="w-4 h-4 text-[#2E9CD6]" />
            {ICS_LABELS[lang] || ICS_LABELS.nl}
          </button>
        </div>
      )}
    </div>
  );
};

const EventCard = ({ ev, lang }) => {
  const key = String(ev.category || "").toLowerCase();
  const meta = EVENT_CATEGORY_META[key] || EVENT_CATEGORY_META.default;
  const dateLabel = formatEventDate(ev, lang);
  const catLabel = eventCategoryLabel(ev.category, lang) || "Evenement";
  const title = pickField(ev, "title", lang);
  const location = pickField(ev, "location", lang);
  const description = pickField(ev, "description", lang);
  return (
    <article
      data-testid={`agenda-event-${ev.slug}`}
      className="bg-white rounded-2xl overflow-hidden border border-[#E4EEF5] hover:shadow-lg transition-shadow duration-300"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[240px] bg-[#F4F8FB]">
          {ev.image ? (
            <img
              src={ev.image}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : null}
          <span
            className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-[Poppins] font-semibold shadow-md"
            style={{ backgroundColor: meta.color, color: meta.textColor }}
          >
            {catLabel}
          </span>
        </div>
        <div className="md:col-span-2 p-5 sm:p-6 flex flex-col">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-wider mb-2">
            <span className="inline-flex items-center gap-1">
              {ev.recurring ? (
                <Repeat className="w-4 h-4" />
              ) : (
                <Calendar className="w-4 h-4" />
              )}
              {dateLabel || ON_REQUEST_LABELS[lang] || ON_REQUEST_LABELS.nl}
            </span>
            {ev.recurring && ev.date && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4F8FB] text-[#5A7594] normal-case tracking-normal font-medium">
                {RECURRING_LABELS[lang] || RECURRING_LABELS.nl}
              </span>
            )}
          </div>
          <h3 className="text-2xl font-[Poppins] font-bold text-[#173A5E] mb-2 leading-tight">
            {title}
          </h3>
          {location && (
            <p className="text-[#5A7594] text-sm font-[Inter] mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#2E9CD6]" />
                {location}
              </span>
              {buildGmapsHref(location) && (
                <a
                  href={buildGmapsHref(location)}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`agenda-gmaps-${ev.slug}`}
                  aria-label={BEKIJK_LOC_LABELS[lang] || BEKIJK_LOC_LABELS.nl}
                  className="inline-flex items-center gap-1 text-[#2E9CD6] hover:text-[#173A5E] font-[Poppins] font-semibold text-xs uppercase tracking-wide transition"
                >
                  {BEKIJK_LOC_LABELS[lang] || BEKIJK_LOC_LABELS.nl}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </p>
          )}
          {description && (
            <p className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed mb-4 whitespace-pre-line">
              {description}
            </p>
          )}
          {ev.link && (
            <a
              href={ev.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`agenda-link-${ev.slug}`}
              className="inline-flex items-center gap-1.5 text-[#2E9CD6] hover:text-[#173A5E] font-[Poppins] font-semibold text-sm"
            >
              {MORE_INFO_LABELS[lang] || MORE_INFO_LABELS.nl}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-auto pt-3">
            {!ev.link && <span />}
            <AddToCalendarButton
              ev={ev}
              lang={lang}
              title={title}
              description={description}
              location={location}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export const AgendaPage = () => {
  const { lang } = useLang();
  const c = pickLang(pageContent.categoryIntros, lang).agenda;
  const seo = AGENDA_SEO[lang] || AGENDA_SEO.nl;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEvents(500);
        if (!cancelled) setEvents(data);
      } catch (e) {
        if (!cancelled)
          setError(ERROR_LABELS[lang] || ERROR_LABELS.nl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return (
    <div data-testid="agenda-page" className="bg-white">
      <Seo title={seo.title} description={seo.description} lang={lang} />
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.eyebrow}</ContentEyebrow>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            {c.title}
          </h1>
          <AccentLine />
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-2xl">
            {c.intro}
          </p>
          <p className="text-[#5A7594] font-[Inter] text-sm mt-4 max-w-2xl">
            {SUBSCRIBE_HINT[lang] || SUBSCRIBE_HINT.nl}
          </p>
          <SubscribeButtons lang={lang} />
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="ll-container max-w-5xl">
          {loading ? (
            <div className="flex items-center gap-2 text-[#5A7594]">
              <Loader2 className="w-4 h-4 animate-spin" />
              {LOAD_LABELS[lang] || LOAD_LABELS.nl}
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420]">
              {error}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 bg-[#F4F8FB] rounded-2xl border border-[#E4EEF5]">
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                <CalendarDays className="w-6 h-6 text-[#2E9CD6]" />
              </div>
              <h3 className="text-xl font-[Poppins] font-bold text-[#173A5E] mb-2">
                {EMPTY_TITLE[lang] || EMPTY_TITLE.nl}
              </h3>
              <p className="text-[#5A7594] font-[Inter] max-w-sm mx-auto">
                {EMPTY_BODY[lang] || EMPTY_BODY.nl}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5">
              {events.map((ev) => (
                <EventCard key={ev.slug} ev={ev} lang={lang} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AgendaPage;